import { css } from '@emotion/react'
import { Alert, HeadingSection, VFlow } from 'bold-ui'
import { FormSectionAccordion } from 'components/accordion/FormSectionAccordion'
import { useAlert } from 'components/alert'
import { ContextualHelpCard } from 'components/ContextualHelpCard'
import { useFlags } from 'config/useFlagsContext'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import iconEsusRegulacao from 'images/contextual-help-card/icon-esus-regulacao.svg'
import { Fragment } from 'react'
import { FormSpy } from 'react-final-form'
import { Route, useHistory, useRouteMatch } from 'react-router'
import { MetaPath } from 'util/metaPath'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'
import { useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { AtendimentoProfissional } from 'view/atendimentos/types/AtendimentoProfissionalModel'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { EsusRegulacaoModalForm } from '../esus-regulacao/esus-regulacao-form/EsusRegulacaoModalForm'
import { IntegracaoRegulacaoCard } from '../esus-regulacao/IntegracaoRegulacaoCard'
import {
  SolicitacaoRegulacaoModel,
  SOLICITAR_ENCAMINHAMENTO_URL,
  TipoSolicitacaoRegulacaoEnum,
} from '../esus-regulacao/model-esusRegulacao'
import { ChecklistEncaminhamento } from './ChecklistEncaminhameto'
import EncaminhamentoExternoEspecializadoForm from './EncaminhamentoExternoEspecializadoForm'
import { EncaminhamentoExternoTable } from './list/EncaminhamentoExternoTable'
import { EncaminhamentoExternoEspecializadoPlanoModel } from './model-encaminhamentoExterno'

export interface EncaminhamentoExternoViewProps {
  name: MetaPath<EncaminhamentoExternoEspecializadoPlanoModel>
  prontuarioId: ID
  atendimentoId: ID
  cidadao: CidadaoAtendimento
  atendimentoProfissional: AtendimentoProfissional
  isAtendimentoOdonto: boolean
}

export default function EncaminhamentoExternoView(props: Readonly<EncaminhamentoExternoViewProps>) {
  const { name, prontuarioId, atendimentoId, cidadao, atendimentoProfissional, isAtendimentoOdonto } = props

  const { PILOTO_VINCULACAO_CUIDADO_ENABLED, API_ESUS_REGULACAO_ENABLED } = useFlags()

  const {
    observacao: { isAtendimentoObservacao },
  } = useAtendimentoContext()

  const { handleSubmit, removeItem } = useEditableListField({
    name: name.encaminhamentosEspecializadosRecentes,
  })

  const alert = useAlert()
  const history = useHistory()
  const match = useRouteMatch()

  const handleSubmitSolicitacaoRegulacao = (_values: SolicitacaoRegulacaoModel) => {
    // TODO (@Sirius): Substituir por chamada à mutation real assim que
    // o endpoint de envio ao e-SUS Regulação existir no backend.
    alert('success', 'Solicitação enviada ao e-SUS Regulação com sucesso.')
  }

  const classes = createStyles()

  return (
    <FormSpy<SoapState>>
      {({ values }) => (
        <Fragment>
          {API_ESUS_REGULACAO_ENABLED && (
            <div css={classes.regulacaoHelpWrapper}>
              <ContextualHelpCard
                title='Agora o e-SUS APS está integrado ao e-SUS Regulação!'
                description={
                  <>
                    Exames, OCIs e Encaminhamentos podem ser enviados ao e-SUS Regulação diretamente pelo Prontuário
                    e-SUS APS. Para isso, basta realizar a solicitação através do card "Solicitar via integração".
                    Depois, acompanhe o andamento das solicitações em{' '}
                    <strong>Garantia do acesso &gt; Regulação assistencial.</strong>
                  </>
                }
                image={iconEsusRegulacao}
              />
            </div>
          )}

          <HeadingSection level={4} title='Encaminhamentos' />

          {API_ESUS_REGULACAO_ENABLED && (
            <Fragment>
              <div css={classes.regulacaoCardWrapper}>
                <IntegracaoRegulacaoCard
                  onAdicionarSolicitacao={() => history.push(`${match.url}${SOLICITAR_ENCAMINHAMENTO_URL}`)}
                />
              </div>

              <Route path={`${match.url}${SOLICITAR_ENCAMINHAMENTO_URL}/:id?`}>
                <EsusRegulacaoModalForm
                  tipoInicial={TipoSolicitacaoRegulacaoEnum.CONSULTA}
                  onClose={() => history.push(match.url)}
                  onSubmit={handleSubmitSolicitacaoRegulacao}
                />
              </Route>
            </Fragment>
          )}

          <VFlow>
            {PILOTO_VINCULACAO_CUIDADO_ENABLED && !isAtendimentoOdonto ? (
              <Alert
                type='warning'
                style={{ padding: '0.5rem', inlineSize: '-webkit-fill-available' }}
                styles={{ container: { alignItems: 'center' } }}
                inline
              >
                <strong>Alteração nos encaminhamentos.</strong> <br />
                Os encaminhamentos devem ser registrados na aba de compartilhamento de cuidado. O histórico dos
                encaminhamentos anteriores ainda está disponível para acesso.
              </Alert>
            ) : (
              <Fragment>
                <ChecklistEncaminhamento name={name.encaminhamentosRecentes} />

                <FormSectionAccordion
                  name={name.openedAccordions}
                  title='Serviço especializado'
                  uuid='encaminhamentoServicoEspecializado'
                  style={classes.accordionEspecializados}
                  buttonStyle={classes.accordionEspecializadosButton}
                >
                  <div css={classes.wrapper}>
                    <EncaminhamentoExternoEspecializadoForm
                      prontuarioId={prontuarioId}
                      onSubmit={handleSubmit}
                      isAtendimentoOdonto={isAtendimentoOdonto}
                      cidadao={cidadao}
                      encaminhamentosCache={values?.plano?.encaminhamentoExterno}
                    />
                  </div>
                </FormSectionAccordion>
              </Fragment>
            )}
            <EncaminhamentoExternoTable
              prontuarioId={prontuarioId}
              atendimentoId={atendimentoId}
              encaminhamentosCache={values?.plano?.encaminhamentoExterno}
              dataReferencia={atendimentoProfissional.iniciadoEm}
              lotacao={atendimentoProfissional.lotacao}
              isAtendimentoObservacao={isAtendimentoObservacao}
              isAtendimentoOdonto={isAtendimentoOdonto}
              atendimentoProfissionalId={atendimentoProfissional.id}
              removeItem={removeItem}
            />
          </VFlow>
        </Fragment>
      )}
    </FormSpy>
  )
}

const createStyles = () => ({
  regulacaoHelpWrapper: css`
    margin-bottom: 1rem;

    &:empty {
      margin-bottom: 0;
    }
  `,
  regulacaoCardWrapper: css`
    margin-top: 1rem;
    margin-bottom: 1rem;
  `,
  wrapper: css`
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: 1rem;
  `,
  accordionEspecializados: css`
    margin-top: 0.5rem;
  `,
  accordionEspecializadosButton: css`
    padding: 0.5rem 1rem;
  `,
})
